import moment from "moment";
import dayjs from "dayjs";

import { normalizeKey } from "../Common/common_helpers";
import { isComparisonSeriesHidden } from "./comparisonSeriesVisibility";
import { getTruncatedName, toTitleCase } from "../Formatters/TextFormatter";

import {
    allMetrics,
    axisColors,
    chartColorCodes,
    decimalPlacesForMetrics,
    disabledMetricsForLevel,
    multiplyFactors
} from '../../Helper/DigitalTwin/DataFile';

import {
    DateDiffInDays,
    formatDateNumeric,
    formatDateWithoutYear,
    getArrayOfFormattedDates,
    getArrayOfNumericDates,
    getWeekNumber
} from "../DatePicker/DateFormatters";

import {
    changeDateFormat,
    convertToZoneDate,
    convertUTCtoZone,
    getDateObject
} from "../DatePicker/DateConverter";

import {
    getBatteryHierarchyV3,
    getBatterySignals,
    getChargerLogData,
    getGranularityDataV2,
    getGranularityDataV3,
    getPackComparisionV4,
    getPackComparisionV5,
} from "../../Api/DigitalTwin";


// This function is required to support both digital twin metric decimals as well as
// for custom comparison metric names
const getDecimalPlacesForMetric = (metric) => {
    if (!metric || typeof metric !== "string") return 0;

    // Extract the part after the last "."
    const parts = metric.split(".");
    const metricName = parts[parts.length - 1];

    // Lookup in the mapping
    if (decimalPlacesForMetrics.hasOwnProperty(metricName)) {
        return decimalPlacesForMetrics[metricName];
    }

    return 2; // default
};

// This function is required to support both digital twin metric decimals as well as
// for custom comparison metric names
const formatYAxisValue = (value, metric) => {

    if (!value) {
        return value
    }

    // Extract the part after the last "."
    const parts = metric.split(".");
    const metricName = parts[parts.length - 1];

    let multiplyFactor = multiplyFactors[metricName] || 1;
    let decimalPlaces = getDecimalPlacesForMetric(metricName);

    let number = parseFloat(value)

    if (!number) {
        return value
    }

    const factor = Math.pow(10, decimalPlaces);
    return Math.trunc((number * multiplyFactor) * factor) / factor;
}

const getToolTipTitle = (primaryGranularity, batteryDataRow, category) => {

    if (primaryGranularity == 'daily') {
        return changeDateFormat(batteryDataRow.range.startDate, 'MMM DD, YYYY')
    }

    if (primaryGranularity == 'weekly') {
        let start = getDateObject(batteryDataRow.range.startDate)
        let end = getDateObject(batteryDataRow.range.endDate)

        if (start.year() === end.year()) {
            return `${start.format("DD MMM")} - ${end.format("DD MMM, YYYY")}`;
        }
        return `${start.format("DD MMM, YYYY")} - ${end.format("DD MMM, YYYY")}`;
    }

    if (primaryGranularity == 'monthly') {
        return changeDateFormat(batteryDataRow.range.startDate, 'MMM YYYY')
    }

    return `${primaryGranularity} - ${category}`
}

const sanitizeChartDataSeries = (data) => {
    const sanitized = Object.values(
        data.reduce((acc, item) => {
            const existing = acc[item.x];
            if (!existing || (existing.y === null && item.y !== null)) {
                acc[item.x] = item;
            }
            return acc;
        }, {})
    );
    return sanitized
}


const formatDigitalTwinChartDataForCategories = ({
    metricKey,
    batteryData,
    primaryGranularity,
    categories,
    selectedMetric,
    chartType
}) => {
    let results = [];

    for (let i in categories) {
        let category = categories[i];
        let row = batteryData[i];
        let metricsArray = row.metricsArray || [];
        metricsArray = Array.isArray(metricsArray) ? metricsArray : []

        let metricData = metricsArray.find(item => item.type === metricKey) || {};
        let metricDataMin = metricsArray.find(item => item.type?.toLowerCase() === `min_${metricKey}`?.toLowerCase()) || {};
        let metricDataMax = metricsArray.find(item => item.type?.toLowerCase() === `max_${metricKey}`?.toLowerCase()) || {};

        let value = metricData.value ?? null
        let metricDataMinValue = metricDataMin.value ? parseFloat(metricDataMin.value) : null
        let metricDataMaxValue = metricDataMax.value ? parseFloat(metricDataMax.value) : null
        let unit = selectedMetric.unit

        value = formatYAxisValue(value, metricKey)
        metricDataMinValue = formatYAxisValue(metricDataMinValue, metricKey)
        metricDataMaxValue = formatYAxisValue(metricDataMaxValue, metricKey)


        let xAxisToolTipLabel = `<span style = "color: #8F8F8F;">Min</span>: ${metricDataMinValue} ${unit}, <span style = "color: #8F8F8F;">Avg</span>: ${value} ${unit}, <span style = "color: #8F8F8F;">Max</span>: ${metricDataMaxValue} ${unit}`

        // `x` is the axis label and is deliberately year-less ("09 Jul", or just "Jul" for
        // monthly), so it cannot identify a bucket on its own. bucketStart keeps the real
        // date for consumers that need one - notably the CSV export, where re-parsing the
        // label gave the year 2001 (V8 resolves a bare day-month that way).
        const bucketStart = row?.range?.startDate ?? null

        if (chartType == "line") {
            results.push({
                x: category,
                y: value,
                avg: value,
                bucketStart,
                tooltipTitle: getToolTipTitle(primaryGranularity, row, categories[i]),
                valueMappings: metricData?.valueMappings,
                xAxisToolTipLabel: xAxisToolTipLabel
            });
            continue
        }

        results.push({
            x: category,
            y: [metricDataMinValue, metricDataMaxValue],
            min: metricDataMinValue,
            max: metricDataMaxValue,
            avg: value,
            bucketStart,
            tooltipTitle: getToolTipTitle(primaryGranularity, row, categories[i]),
            xAxisToolTipLabel: xAxisToolTipLabel
        });
    }

    return sanitizeChartDataSeries(results);
}

const formatDigitalTwinChartDataForGranularity = (metricKey, batteryData, categories = [], primaryGranularity) => {
    let results = [];

    for (let i in batteryData) {
        let row = batteryData[i];
        let metricsArray = row.metricsArray || [];

        if (!Array.isArray(metricsArray) || metricsArray.length === 0) {
            continue;
        }

        let metricData = metricsArray.find(item => item.type?.toLowerCase() === metricKey?.toLowerCase()) || {};

        results.push({
            x: categories[i],
            y: formatYAxisValue(metricData['value'], metricKey),
            // See formatDigitalTwinChartDataForCategories: the axis label carries no year.
            bucketStart: row?.range?.startDate ?? null,
            tooltipTitle: getToolTipTitle(primaryGranularity, row, categories[i]),
            valueMappings: metricData?.valueMappings,
        });
    }

    let sanitizeChartData = sanitizeChartDataSeries(results);

    return sanitizeChartData;
};

const formatDigitalTwinChartCategoriesForGranularity = (batteryData, primaryGranularity) => {
    let categories = [];
    for (let i in batteryData) {
        let row = batteryData[i];

        if (!row?.range?.startDate) {
            continue
        }

        if (primaryGranularity == "monthly") {
            const month = moment(row?.range?.startDate, "YYYY-MM");
            categories.push(moment(month, "YYYY-MM").format("MMM"));
        } else {
            const date = new Date(row?.range?.startDate);
            categories.push(formatDateWithoutYear(date));
        }

    }

    return categories;
};

const digitalTwinChartDataForAvgGranularity = (metricKeys, batteryData, categories, primaryGranularity) => {
    let results = {}
    for (let i in metricKeys) {
        let metricKey = metricKeys[i]
        results[metricKey] = formatDigitalTwinChartDataForGranularity(metricKey, batteryData, categories, primaryGranularity)
    }

    return results;
}

const getDigitalTwinChartDataForGranularity = async (args) => {
    const { batteryID, selectedNode, selectedDate, selectedMetrics, metricKeys, primaryGranularity, secondaryGranularity } = args
    const dates = selectedDate?.split(":");
    if (!dates || dates.length < 2 || !dates[0] || !dates[1]) {
        return [[], {}, []]
    }
    const startDate = dates[0]
    const endDate = dates[1]

    const response = await getGranularityDataV2(
        batteryID,
        selectedNode.id,
        selectedNode.level?.toLowerCase(),
        metricKeys,
        [startDate, endDate],
        toTitleCase(primaryGranularity),
        'avg' // We can not send any mix granularity like avg-min-min it will break the backend API
    )

    const batteryData = response.response.batteryData

    const categories = formatDigitalTwinChartCategoriesForGranularity(batteryData, primaryGranularity)

    if (secondaryGranularity == 'avg') {
        const metricsData = digitalTwinChartDataForAvgGranularity(metricKeys, batteryData, categories, primaryGranularity)
        let seriesData = metricKeys
            .map(key => selectedMetrics.find(metric => metric.key.toLowerCase() === key.toLowerCase()))
            .filter(Boolean)
            .map(metric => ({ ...metric, data: metricsData[metric.key] }));

        return [seriesData, metricsData, categories]
    }

    let metricsData = {}
    let seriesData = []

    for (let i in metricKeys) {
        let metricKey = metricKeys[i]
        let selectedMetric = selectedMetrics.find(metric => metric.key.toLowerCase() === metricKey.toLowerCase())
        metricsData[metricKey] = formatDigitalTwinChartDataForCategories({ metricKey, batteryData, primaryGranularity, categories, selectedMetric, chartType: 'line' },)
        metricsData[`${metricKey}_mix`] = formatDigitalTwinChartDataForCategories({ metricKey, batteryData, primaryGranularity, categories, selectedMetric, chartType: 'area' })
    }

    let lineSeries = metricKeys
        .map(key => selectedMetrics.find(metric => metric.key === key))
        .filter(Boolean)
        .map(metric => ({ ...metric, type: 'line', data: metricsData[metric.key] }));

    let areaSeries = metricKeys
        .map(key => selectedMetrics.find(metric => metric.key === key))
        .filter(Boolean)
        .map(metric => ({ ...metric, name: `${metric.name} mix`, type: 'rangeArea', data: metricsData[`${metric.key}_mix`] }));

    seriesData = lineSeries.concat(areaSeries)

    return [seriesData, metricsData, categories]

}

const getDigitalTwinChartDataForGranularityForComparison = async (args) => {
    const { batteryID, selectedNodes, selectedDate, metricKeys, primaryGranularity, secondaryGranularity, selectedMetrics, signal } = args
    const dates = selectedDate?.split(":");

    if (!dates || dates.length < 2 || !dates[0] || !dates[1]) {
        return [[], {}, []]
    }
    const startDate = dates[0]
    const endDate = dates[1]

    const reqPrimaryGranularity = toTitleCase(primaryGranularity)

    // When min-avg-max is requested the API only supports 'avg' as the operation
    // but returns min/avg/max values in the response — same as single-node vitals.
    const apiOperation = secondaryGranularity === 'min-avg-max' ? 'avg' : secondaryGranularity;

    const systemIDs = selectedNodes.map(node => node.id);
    const level = selectedNodes[0]?.level?.toLowerCase();

    const response = await getGranularityDataV3(
        batteryID,
        systemIDs,
        level,
        metricKeys,
        [startDate, endDate],
        reqPrimaryGranularity,
        apiOperation,
        signal
    );

    const batteryDataForIds = response?.response?.batteryData || {};

    const firstBatteryData = batteryDataForIds[selectedNodes[0].id];
    const categories = formatDigitalTwinChartCategoriesForGranularity(firstBatteryData, primaryGranularity)

    let nodeMetricsData = {}
    let metricsSeriesData = {}

    for (let i in selectedNodes) {
        let node = selectedNodes[i]
        let metricsData = {}
        const nodeBatteryData = batteryDataForIds[node.id] ?? [];
        if (secondaryGranularity === 'min-avg-max') {
            for (let metricKey of metricKeys) {
                const selectedMetric = selectedMetrics.find(m => m.key.toLowerCase() === metricKey.toLowerCase())
                metricsData[metricKey] = formatDigitalTwinChartDataForCategories({ metricKey, batteryData: nodeBatteryData, primaryGranularity, categories, selectedMetric, chartType: 'line' })
                metricsData[`${metricKey}_mix`] = formatDigitalTwinChartDataForCategories({ metricKey, batteryData: nodeBatteryData, primaryGranularity, categories, selectedMetric, chartType: 'area' })
            }
        } else {
            metricsData = digitalTwinChartDataForAvgGranularity(metricKeys, nodeBatteryData, categories, primaryGranularity)
        }
        nodeMetricsData[node.id] = metricsData
    }

    let metrics = metricKeys
        .map(key => selectedMetrics.find(metric => metric.key.toLowerCase() === key.toLowerCase()))

    for (let i in metrics) {
        let metric = metrics[i]
        let metricSeriesData = []
        for (let j in selectedNodes) {
            let node = selectedNodes[j]
            const nodeColor = chartColorCodes[j] || '#000000'
            // Shared by the line and its rangeArea companion, and identical for this node
            // in every metric; the comparison legend hides series by it. See
            // comparisonSeriesVisibility.js for why the display name is not enough.
            const legendKey = `${j}:${node.id}`
            metricSeriesData.push({
                ...metric,
                name: `${node.label} : ${node.displayId}`,
                legendKey,
                color: nodeColor,
                data: nodeMetricsData[node.id][metric.key]
            })
            if (secondaryGranularity === 'min-avg-max') {
                metricSeriesData.push({
                    ...metric,
                    name: `${node.label} : ${node.displayId} mix`,
                    legendKey,
                    type: 'rangeArea',
                    color: nodeColor,
                    data: nodeMetricsData[node.id][`${metric.key}_mix`]
                })
            }
        }
        metricsSeriesData[metric.key] = metricSeriesData
    }

    return [[], metricsSeriesData, categories]

}

export const formatDigitalTwinChartData = (metricKey, batteryData, chargerData = {}) => {
    let results = [];
    for (let i in batteryData) {
        let row = batteryData[i];
        let metricsArray = row.metricsArray || [];
        let metricData = metricsArray.find(item => item.type?.toLowerCase() === metricKey?.toLowerCase()) || {};
        let xAxisValue = convertUTCtoZone(row.batteryDateTime, true)
        let chargerValues = chargerData[xAxisValue] || {}

        let chartRow = {
            x: xAxisValue,
            y: formatYAxisValue(metricData.value ?? null, metricKey), // Use null if value is undefined,
            tooltipTitle: xAxisValue,
            valueMappings: metricData?.valueMappings,
            valuesToAppend: {}
        }

        if (chargerValues && chargerValues.chargerDisplayName) {
            chartRow.valuesToAppend = {
                CabinetID: chargerValues.chargerDisplayName,
                SlotID: chargerValues.slotDisplayName
            }
        }

        results.push(chartRow);
    }
    return results;
};

const formatChargerData = (chargerDataResponse) => {
    let chargerResponseData = []

    if (chargerDataResponse?.response?.data && chargerDataResponse?.response?.data.length > 0) {
        chargerResponseData = chargerDataResponse?.response?.data
    }

    let chargerData = {}

    for (let i in chargerResponseData) {
        let row = chargerResponseData[i]
        if (row && row.batteryDateTime) {
            let batteryDateTime = convertUTCtoZone(row.batteryDateTime, true)
            chargerData[batteryDateTime] = row
        }
    }

    return chargerData
}

const getDigitalTwinChartDataForRaw = async (args) => {
    const { batteryID, selectedNode, selectedDate, metricKeys, selectedMetrics, startDate, endDate } = args

    let resampleSeconds = args.intervalSeconds || 300
    let systemID = selectedNode?.id
    let level = selectedNode.level?.toLowerCase()

    // Check if startDate and endDate have a difference of more than 24 hours
    let startTime = selectedDate;
    let endTime = selectedDate;
    if (startDate && endDate) {
        const start = dayjs(startDate);
        const end = dayjs(endDate);
        const hoursDiff = end.diff(start, 'hours');

        if (hoursDiff < 24) {
            startTime = startDate;
            endTime = endDate;
        }
    }

    let dates = [startTime, endTime]

    const [response, chargerDataResponse] = await Promise.all([
        getPackComparisionV4(batteryID, systemID, level, metricKeys, dates, resampleSeconds),
        getChargerLogData(batteryID, null, startTime, endTime, resampleSeconds)
    ]);

    if (!response) {
        return [[], {}, []]
    }

    let chargerData = formatChargerData(chargerDataResponse)

    const batteryData = response?.response?.batteryData || []

    let metricsData = {}

    for (let i in metricKeys) {
        let metricKey = metricKeys[i]
        metricsData[metricKey] = formatDigitalTwinChartData(metricKey, batteryData, chargerData)
    }

    let seriesData;

    seriesData = metricKeys
        .map(key => selectedMetrics.find(metric => metric.key.toLowerCase() === key.toLowerCase()))
        .filter(Boolean)
        .map(metric => ({ ...metric, data: metricsData[metric.key] }));

    return [seriesData, metricsData, []]
}

const getDigitalTwinChartDataForRawInComparison = async (args) => {
    const { batteryID, selectedNodes, selectedDate, metricKeys, selectedMetrics, signal, startDate, endDate } = args

    let resampleSeconds = args.intervalSeconds || 300

    // Check if startDate and endDate have a difference of more than 24 hours
    let startTime = selectedDate;
    let endTime = selectedDate;
    if (startDate && endDate) {
        const start = dayjs(startDate);
        const end = dayjs(endDate);
        const hoursDiff = end.diff(start, 'hours');

        if (hoursDiff < 24) {
            startTime = startDate;
            endTime = endDate;
        }
    }

    let dates = [startTime, endTime]

    const systemIDs = selectedNodes.map(node => node.id);
    const level = selectedNodes[0]?.level?.toLowerCase();

    const response = await getPackComparisionV5(batteryID, systemIDs, level, metricKeys, dates, resampleSeconds, signal);
    const batteryDataForIds = response?.response?.batteryData || {};

    let nodeMetricsData = {}
    let metricsSeriesData = {}

    for (let i in selectedNodes) {
        let node = selectedNodes[i]
        let metricsData = {}
        for (let j in metricKeys) {
            let metricKey = metricKeys[j]
            metricsData[metricKey] = formatDigitalTwinChartData(metricKey, batteryDataForIds[node.id])
        }
        nodeMetricsData[node.id] = metricsData
    }

    let metrics = metricKeys
        .map(key => selectedMetrics.find(metric => metric.key.toLowerCase() === key.toLowerCase()))


    for (let i in metrics) {
        let metric = metrics[i]
        let metricSeriesData = []
        for (let j in selectedNodes) {
            let node = selectedNodes[j]
            metricSeriesData.push({
                ...metric,
                name: `${node.label} : ${node.displayId}`,
                // Same key scheme as the granularity builder; see comparisonSeriesVisibility.js.
                legendKey: `${j}:${node.id}`,
                color: chartColorCodes[j] || '#000000',
                data: nodeMetricsData[selectedNodes[j].id][metric.key]
            })
        }
        metricsSeriesData[metric.key] = metricSeriesData
    }


    return [[], metricsSeriesData, []]
}

const generateDailyBuckets = (startDate, endDate, bucketSizeInDays) => {
    const tempFormatted = getArrayOfFormattedDates(startDate, endDate);
    const tempNumeric = getArrayOfNumericDates(startDate, endDate);

    const buckets = [];
    const tempDatesToShow = [];
    const tempDatesToSave = [];

    for (let i = 0; i < tempFormatted.length; i += bucketSizeInDays) {
        const tempStart = tempFormatted[i];
        const tempEnd = tempFormatted[i + bucketSizeInDays - 1] || tempFormatted[tempFormatted.length - 1];

        const tempStartSave = tempNumeric[i];
        const tempEndSave = tempNumeric[i + bucketSizeInDays - 1] || tempNumeric[tempNumeric.length - 1];

        buckets.push({
            start: tempStart,
            end: tempEnd,
            textToShow: `${tempStart} - ${tempEnd}`,
            textToSave: `${tempStartSave}:${tempEndSave}`,
        });

        tempDatesToShow.push(`${tempStart} - ${tempEnd}`);
        tempDatesToSave.push(`${tempStartSave}:${tempEndSave}`);
    }

    return [tempDatesToSave[0], tempDatesToShow, tempDatesToSave];
}

const generateWeeklyBuckets = (startDate, endDate, bucketSizeInWeeks) => {

    const bucketSizeInDays = bucketSizeInWeeks * 7;
    const start = new Date(startDate);
    const end = new Date(endDate);
    const finalRanges = [];

    const createBucket = (start, end) => ({
        start: new Date(start),
        end: new Date(end),
        startWeek: getWeekNumber(start),
        endWeek: getWeekNumber(end),
        textToShow: moment(start).format("DD MMM YYYY") + " - " + moment(end).format("DD MMM YYYY"),
        textToSave: formatDateNumeric(start) + ":" + formatDateNumeric(end),
    });

    const returnFormatted = (buckets) => {
        const textToShowArray = buckets.map(item => item.textToShow);
        const textToSaveArray = buckets.map(item => item.textToSave);
        return [textToSaveArray[0], textToShowArray, textToSaveArray];
    };

    // Step 1: Align the first bucket to include partial days before first Monday
    const firstMonday = new Date(start);
    const day = firstMonday.getDay();
    const diffToMonday = day === 0 ? -6 : 1 - day; // move back to Monday
    firstMonday.setDate(firstMonday.getDate() + diffToMonday);

    let currentStart = new Date(start); // Include original start in first bucket
    let currentEnd = new Date(firstMonday);
    currentEnd.setDate(currentEnd.getDate() + bucketSizeInDays - 1); // end of 12-week span

    while (currentStart <= end) {
        let bucketEnd = new Date(currentEnd);
        if (bucketEnd > end) {
            bucketEnd = new Date(end); // last shorter bucket
        }

        finalRanges.push(createBucket(currentStart, bucketEnd));

        // next bucket
        currentStart = new Date(currentEnd);
        currentStart.setDate(currentStart.getDate() + 1);
        currentEnd = new Date(currentStart);
        currentEnd.setDate(currentStart.getDate() + bucketSizeInDays - 1);
    }

    return returnFormatted(finalRanges);
};

const generateMonthlyBuckets = (startDate, endDate) => {
    let tempStartYear = convertToZoneDate(startDate).getFullYear();
    let tempEndYear = convertToZoneDate(endDate).getFullYear();
    let tempYears = [];
    for (var i = tempStartYear; i <= tempEndYear; i++) tempYears.push(i);
    let tempDateRanges = [];
    tempYears.map((item) => {
        var theFirst = new Date(item, 0, 1).toDateString();
        var theLast = new Date(item, 11, 31).toDateString();
        tempDateRanges.push({ start: theFirst, end: theLast, year: item });
    });

    tempDateRanges[0]["start"] = convertToZoneDate(startDate);
    tempDateRanges[tempDateRanges.length - 1]["end"] = convertToZoneDate(endDate);
    let finalRanges = [],
        tempDatesToShow = [],
        tempDatesToSave = [];
    tempDateRanges.map((item) => {
        var tempYear = item.year,
            tempShow = formatDateNumeric(new Date(item.start)) + " : " + formatDateNumeric(new Date(item.end));
        finalRanges.push({
            start: item.start,
            end: item.end,
            textToShow: tempYear,
            textToSave: tempShow,
        });

        tempDatesToShow.push(tempYear);
        tempDatesToSave.push(tempShow);
    });

    return [tempDatesToSave[0], tempDatesToShow, tempDatesToSave]
}

const getYAxisValuesFromData = (data) => {
    let values = []
    for (let i in data) {
        let item = data[i]
        let y = null
        if (!item) {
            continue
        }

        if (typeof item === 'object') {
            y = item.y
        }

        if (typeof item === 'number' || typeof item === 'string') {
            y = item
        }

        if (y !== null && y !== undefined) {
            const num = Number(y)
            if (!isNaN(num)) {
                values.push(num)
            }
        }
    }

    return values
}

export const shouldDisableGranularity = (granularity, startDate, endDate) => {
    if (granularity === 'raw') {
        return false
    }

    let daysDiff = DateDiffInDays(startDate, endDate)

    if (granularity == 'daily' && daysDiff > 1) {
        return false
    }

    if (granularity == 'weekly' && daysDiff >= 15) {
        return false
    }

    if (granularity == 'monthly' && daysDiff >= 30) {
        return false
    }
    return true
}

export const getDigitalTwinChartDataForComparison = async (args) => {
    try {

        if (!args.startDate && !args.endDate) {
            return [[], {}, []]
        }

        if (args.primaryGranularity === 'raw') {
            return getDigitalTwinChartDataForRawInComparison(args)
        }

        return getDigitalTwinChartDataForGranularityForComparison(args)

    } catch (err) {
        console.error(err)
    }
}

export const getDigitalTwinChartData = async (args) => {
    try {

        if (!args.startDate && !args.endDate) {
            return [[], {}, []]
        }

        if (args.primaryGranularity === 'raw') {
            return getDigitalTwinChartDataForRaw(args)
        }

        return getDigitalTwinChartDataForGranularity(args)

    } catch (err) {
        console.log(err)
    }

    return [[], {}, []]
}

export const transformTwinHierarchyData = (data, trans, level = 0, parentId = '') => {
    const result = [];

    // Sort data by sequence if available, otherwise by systemID
    const sortedData = data?.slice().sort((a, b) => {
        // Use sequence for sorting if it exists
        if (a.sequence !== undefined && b.sequence !== undefined) {
            return a.sequence - b.sequence;
        }
        // Fall back to systemID sorting
        const idA = parseInt(a.systemID);
        const idB = parseInt(b.systemID);
        return idA - idB;
    }) || [];

    sortedData.forEach((item, index) => {
        const currentId = item.systemID + '';
        // Use sequence if available, otherwise use index
        const displayNumber = item.sequence !== undefined ? item.sequence : (index + 1);
        let label = toTitleCase(`${trans(`digital_twin:${normalizeKey(item.level)}`)} ${displayNumber}`);
        if (['battery', 'vehicle', 'tms'].includes(item.level.toLowerCase())) {
            label = item.displayName;
        }
        const node = {
            id: currentId,
            label: label,
            level: item.level,
            info: item.info,
            displayId: item.displayName
        };

        if (item.content && Array.isArray(item.content)) {
            node.children = transformTwinHierarchyData(item.content, trans, level + 1, currentId);
        }

        result.push(node);
    });

    return result;
}

export const getNodesByIds = (tree, targetIds) => {
    const result = [];
    const idSet = new Set(targetIds);

    function traverse(nodes) {
        for (const node of nodes) {
            if (idSet.has(node.id)) {
                result.push(node);
            }
            if (node.children) {
                traverse(node.children);
            }
        }
    }

    traverse(tree);
    return result;
}

export const calculateCarouselData = (startDate, endDate, primaryGranularity) => {
    if (primaryGranularity === 'raw') {
        if (startDate || endDate) {
            var tempFormatted = getArrayOfFormattedDates(startDate, endDate);
            var tempNumeric = getArrayOfNumericDates(startDate, endDate);
            return [tempNumeric[0], tempFormatted, tempNumeric]
        }
    }

    if (primaryGranularity === 'daily') {
        return generateDailyBuckets(startDate, endDate, 15)
    }

    if (primaryGranularity === "weekly") {
        return generateWeeklyBuckets(startDate, endDate, 12)
    }

    if (primaryGranularity === "monthly") {
        return generateMonthlyBuckets(startDate, endDate)
    }

    return [undefined, [], []]

}

export const getHierarchyInfo = (batteryConfig) => {
    let displayPackCount = batteryConfig.displayPackCount;
    let displayModuleCount = (displayPackCount || 1) * batteryConfig.displayModuleCount;
    let displayCellCount = (displayPackCount || 1) * (batteryConfig.displayModuleCount || 1) * (batteryConfig.displayCellCount || 1);

    if (batteryConfig.buildingBlockType == 'pc') {
        return `Packs: ${displayPackCount}, Cells: ${displayCellCount}`
    }

    return `Packs: ${displayPackCount}, Modules: ${displayModuleCount}, Cells: ${displayCellCount}`
}

export const getLeveledMetrics = (level, pageContent, trans = () => { }) => {

    let availableMetricsForOrg = pageContent?.metaInfo?.availableMetrics || []

    let translatedMetrics = allMetrics.map((metric) => ({ ...metric, label: trans(`digital_twin:metrics.${normalizeKey(metric.label)}`, metric.label) }))

    if (availableMetricsForOrg && availableMetricsForOrg.length > 0) {
        translatedMetrics = translatedMetrics.filter(metric => availableMetricsForOrg.includes(metric.key))
    }

    if (!level) {
        return translatedMetrics;
    }

    let levelDisabledMetrics = disabledMetricsForLevel[level] ?? [];

    let nodeMetrics = translatedMetrics.filter(metric => !levelDisabledMetrics.includes(metric.key))
    const ranges = pageContent?.metaInfo?.batteryVitalsRanges?.[level];
    if (!ranges) {
        return nodeMetrics;
    }
    return nodeMetrics.map((metric) => ({
        ...metric,
        ranges: ranges[metric.key] || metric.ranges,
    }));
}

export const getTooltipHtml = ({ series, seriesIndex, dataPointIndex, w }, metrics, t, visibleSeries = []) => {

    const metricNames = metrics.map((metric) => metric.name)
    const metricByName = metrics.reduce((acc, metric) => {
        acc[metric.name] = metric
        return acc
    }, {})
    let title = "No Title"
    let hoverItems = [];
    let tooltipItemContainerClass = 'tooltip-item-container';
    const hoverXaxis = w.globals.seriesX[seriesIndex][dataPointIndex];
    let currentSeriesDataPoint = w?.config?.series[seriesIndex]['data'][dataPointIndex]

    if (currentSeriesDataPoint && currentSeriesDataPoint?.tooltipTitle) {
        title = currentSeriesDataPoint?.tooltipTitle
    }

    const hoverIndexes = w.globals.seriesX.map(seriesX => {
        return seriesX.findIndex(xData => xData === hoverXaxis);
    });

    hoverIndexes.forEach((hoverIndex, seriesEachIndex) => {
        let seriesName = w.globals.seriesNames[seriesEachIndex]
        if (!metricNames.includes(seriesName)) {
            return;
        }
        if (visibleSeries.length && !visibleSeries[seriesEachIndex]) {
            return;
        }

        let value = series[seriesEachIndex][hoverIndex];
        if (value == undefined) {
            return
        }

        let currentSeries = w.config.series[seriesEachIndex]
        const seriesColor = w.globals.colors[seriesEachIndex] || '#4D44D8';
        const metricForSeries = metricByName[seriesName] || metrics.find(metric => metric.key === currentSeries?.key);
        const unit = metricForSeries?.unit ?? currentSeries?.unit ?? '';
        
        seriesName = t(`digital_twin:metrics.${normalizeKey(seriesName)}`, seriesName)

        // value = `${value} ${currentSeries['unit']}`
        value = unit ? `${value} ${unit}` : value;
        const valueMappings = currentSeries['data'][hoverIndex]['valueMappings']

        if (valueMappings) {
            let key = String(value).trim().toLowerCase()
            value = valueMappings[key] ?? value
        }

        if (currentSeries['data'][hoverIndex] && currentSeries['data'][hoverIndex]['xAxisToolTipLabel']) {
            value = currentSeries['data'][hoverIndex]['xAxisToolTipLabel'];
        }

        hoverItems.push(`
                        <div class="${tooltipItemContainerClass}">
                            <span class="dot" style="background-color: ${seriesColor};"></span>
                            <span style = "color #444B51;" class="tooltip-item-text">
                                ${seriesName}: <strong>${value ?? 'NA'}</strong>
                            </span>
                        </div>
        `);
    });


    let valuesToAppend = currentSeriesDataPoint?.valuesToAppend || {}

    Object.entries(valuesToAppend).forEach(([key, value]) => {
        hoverItems.push(
            `<div class="${tooltipItemContainerClass}">
                <span class="dot" style=" display: none; background-color: ${'#FF1493'};"></span>
                <span class="tooltip-item-text">
                    ${key}: <strong>${value ?? 'NA'}</strong>
                </span>
            </div>`
        )
    })

    let hoverListHtml = hoverItems.join('');

    return `
        <div>
            <span>
                <div class="apexcharts-tooltip-title" style="font-family: Helvetica, Arial, sans-serif; font-size: 12px">
                    ${title}
                </div>
            </span>
            </div>
            <div class="tooltip-container">
                ${hoverListHtml}
            </div>
        </div>`
}

/**
 * Pick the series whose x value at `dataPointIndex` anchors the shared tooltip. Apex's
 * `seriesIndex` is used when that series is visible and has a point there; otherwise the
 * first visible line series with a point, then the first visible series of any type.
 * Returns -1 when no visible series has a point at that index.
 */
export const resolveComparisonHoverSeriesIndex = (seriesIndex, dataPointIndex, w, isHidden) => {
    const seriesX = w?.globals?.seriesX || [];
    const configSeries = w?.config?.series || [];
    const hasPoint = (i) => !isHidden(i) && seriesX[i] && seriesX[i][dataPointIndex] !== undefined;

    if (seriesIndex !== undefined && seriesIndex !== null && seriesIndex >= 0 && hasPoint(seriesIndex)) {
        return seriesIndex;
    }
    const lineIndex = seriesX.findIndex((_, i) => configSeries[i]?.type !== 'rangeArea' && hasPoint(i));
    if (lineIndex !== -1) {
        return lineIndex;
    }
    return seriesX.findIndex((_, i) => hasPoint(i));
};

// hiddenSeriesKeys: legend keys (see comparisonSeriesVisibility.js) of series switched off
// in the comparison legend. Defaults to none, so every other caller is unaffected. Series
// Apex itself collapsed (w.globals.collapsedSeriesIndices) are skipped as well.
export const getTooltipHtmlForComparison = ({ series, seriesIndex, dataPointIndex, w }, t, hiddenSeriesKeys = new Set()) => {

    let title = "No Title"
    let hoverItems = [];
    let tooltipItemContainerClass = 'tooltip-item-container';

    const collapsedIndices = w?.globals?.collapsedSeriesIndices || [];
    const isHidden = (index) =>
        collapsedIndices.includes(index) ||
        isComparisonSeriesHidden(w?.config?.series?.[index], hiddenSeriesKeys);

    const anchorIndex = resolveComparisonHoverSeriesIndex(seriesIndex, dataPointIndex, w, isHidden);
    if (anchorIndex === -1) {
        return '';
    }

    const hoverXaxis = w.globals.seriesX[anchorIndex][dataPointIndex];
    let currentSeriesDataPoint = w?.config?.series?.[anchorIndex]?.['data']?.[dataPointIndex]

    if (currentSeriesDataPoint && currentSeriesDataPoint?.tooltipTitle) {
        title = currentSeriesDataPoint?.tooltipTitle
    }

    const hoverIndexes = w.globals.seriesX.map(seriesX => {
        return seriesX.findIndex(xData => xData === hoverXaxis);
    });

    hoverIndexes.forEach((hoverIndex, seriesEachIndex) => {
        let currentSeries = w.config.series[seriesEachIndex]
        if (currentSeries.type === 'rangeArea') return
        if (isHidden(seriesEachIndex)) return
        let seriesName = w.globals.seriesNames[seriesEachIndex]
        let value = series[seriesEachIndex][hoverIndex];
        const seriesColor = w.globals.colors[seriesEachIndex] || '#4D44D8';
        seriesName = t(`digital_twin:metrics.${normalizeKey(seriesName)}`, seriesName)
        // In min-avg-max mode the point carries a pre-rendered label that already spells
        // out "Min: 23 %, Avg: 67 %, Max: 99 %" - unit included after each of the three.
        const rangeLabel = currentSeries['data'][hoverIndex]?.['xAxisToolTipLabel']
        if (rangeLabel) {
            value = rangeLabel;
        }

        if (value == undefined) {
            return
        }

        // Only a bare numeric value needs the unit appended. Doing it unconditionally
        // added a second one to the pre-rendered label, rendering "Max: 99 % %".
        if (!rangeLabel) {
            value = `${value} ${currentSeries.unit}`
        }

        const valueMappings = currentSeries['data'][hoverIndex]['valueMappings']

        if (valueMappings) {
            let key = String(value).trim().toLowerCase()
            value = valueMappings[key] ?? value
        }

        hoverItems.push(`
                        <div class="${tooltipItemContainerClass}">
                            <span class="dot" style="background-color: ${seriesColor};"></span>
                            <span class="tooltip-item-text">
                                ${seriesName}: <strong>${value ?? 'NA'}</strong>
                            </span>
                        </div>
        `);
    });


    let length = hoverItems.length;

    if (length > 14) {
        const third = Math.ceil(length / 3);
        const firstCol = hoverItems.slice(0, third);
        const secondCol = hoverItems.slice(third, 2 * third);
        const thirdCol = hoverItems.slice(2 * third);

        return `
        <div class="comparison-tooltip-container">
            <span>
                <div class="apexcharts-tooltip-title" style="font-family: Helvetica, Arial, sans-serif; font-size: 12px">
                    ${title}
                </div>
            </span>
        </div>
        <div class="tooltip-container" style="display: flex; flex-direction: row; gap: 20px;">
            <div style="display: flex; flex-direction: column; gap: 10px;">
                ${firstCol.join('')}
            </div>
            <div style="display: flex; flex-direction: column; gap: 10px;">
                ${secondCol.join('')}
            </div>
            <div style="display: flex; flex-direction: column; gap: 10px;">
                ${thirdCol.join('')}
            </div>
        </div>`;
    }

    if (length >= 7) {
        let halfHoverItems = hoverItems.slice(0, Math.ceil(length / 2));
        let secondHoverItems = hoverItems.slice(Math.ceil(length / 2));
        return `
            <div class = "comparison-tooltip-container">
                <span>
                    <div class="apexcharts-tooltip-title" style="font-family: Helvetica, Arial, sans-serif; font-size: 12px">
                        ${title}
                    </div>
                </span>
                </div>
                <div class="tooltip-container" style = "display:flex; flex-direction: row;">
                    <div style = "display: flex; flex-direction: column; gap: 10px">
                        ${halfHoverItems.join('')}
                    </div>
                    <div style = "display: flex; flex-direction: column; gap: 10px">
                        ${secondHoverItems.join('')}
                    </div>

                </div>
            </div>`
    }

    let hoverListHtml = hoverItems.join('');

    // don't show tooltip if there is no data
    if (length === 0) {
        return '';
    }

    return `
        <div>
            <span>
                <div class="apexcharts-tooltip-title" style="font-family: Helvetica, Arial, sans-serif; font-size: 12px">
                    ${title}
                </div>
            </span>
            </div>
            <div class="tooltip-container">
                ${hoverListHtml}
            </div>
        </div>`
}

/**
 * Rows for a continuous-signals CSV. `entityId` is the identifier column:
 * { label, value }, e.g. { label: "Registration No", value: "MH12AB1234" }.
 * The label falls back to "Device ID".
 */
export const getCSVDataForExportFromChart = (entityId, seriesData, secondaryGranularity, trans) => {

    const sanitizeValue = (val) => {
        if (val === null || val === undefined) return "";

        if (Array.isArray(val)) {
            const cleaned = val
                .filter(v => v !== null && v !== undefined)
                .map(v => String(v).replace(/,/g, " ").trim());

            return cleaned.length > 0 ? cleaned.join(" - ") : "";
        }

        return String(val).replace(/,/g, " ").trim();
    };

    const mapValueForColumn = (point, value) => {
        if (point.valueMappings) {
            let key = String(value).trim().toLowerCase()
            return point.valueMappings[key] ?? value
        }
        return value;
    }

    const sanitizeMetricNameForColumn = (series, name) => {
        if (!name) return "";
        return String(name)
            .replace(/,/g, " ")
            .replace(/mix/gi, "(min - max)")
            .trim();
    };

    const isAreaChart = () => {
        return secondaryGranularity === 'min-avg-max'
    }

    const formattedSeriesData = () => {
        if (isAreaChart()) {
            return seriesData.filter((series) => series.type === 'rangeArea')
        }
        return seriesData
    }

    const idColumn = entityId?.label || trans('common:device_id')
    const timestampMap = new Map();

    for (const series of formattedSeriesData(seriesData)) {
        const metricName = sanitizeMetricNameForColumn(series, series.label);
        for (const point of series.data) {
            const timestamp = point.x;
            const sanitizedTimestamp = sanitizeValue(timestamp);

            if (!timestampMap.has(timestamp)) {
                timestampMap.set(timestamp, {
                    [idColumn]: sanitizeValue(entityId?.value),
                    Date: sanitizedTimestamp
                });
            }

            if (isAreaChart()) {
                timestampMap.get(timestamp)[`${metricName} - Min`] = sanitizeValue(point.min);
                timestampMap.get(timestamp)[`${metricName} - Avg`] = sanitizeValue(point.avg);
                timestampMap.get(timestamp)[`${metricName} - Max`] = sanitizeValue(point.max);
                continue;
            }

            let yAxisValue = sanitizeValue(point.y);

            timestampMap.get(timestamp)[metricName] = mapValueForColumn(point, yAxisValue);

        }
    }

    return Array.from(timestampMap.values());
};

export const shouldDisabledNode = (level, batteryConfig) => {
    if (level == 'cell' && batteryConfig?.disableCells) {
        return true
    }

    if (level == 'module' && batteryConfig?.disableModules) {
        return true
    }

    if (level == 'pack' && batteryConfig?.disablePacks) {
        return true
    }

    return false
}

export const calculateDynamicRange = (data, minYForOrg, maxYForOrg) => {

    let defaultValues = {
        min: minYForOrg,
        max: maxYForOrg
    }

    if (!data || data.length === 0) {
        return defaultValues
    }

    let yAxisValue = getYAxisValuesFromData(data)

    if (!yAxisValue || yAxisValue.length === 0) {
        return defaultValues
    }

    let minYInData = Math.min(...yAxisValue);
    let maxYInData = Math.max(...yAxisValue);

    if (minYInData > minYForOrg && maxYInData < maxYForOrg) {
        return defaultValues
    }

    let maxValue = maxYInData * 1.01
    let minValue = minYInData * 0.99

    if (minYInData >= 100000) {
        maxValue = maxYInData * 1.0001
        minValue = minYInData * 0.9999
    }

    let minValueSlice = Math.floor(minValue / 5) * 5
    let maxValueSlice = Math.ceil(maxValue / 5) * 5


    let min = minValueSlice
    let max = maxValueSlice

    if (typeof (min) != 'number' || typeof (max) != 'number') {
        return defaultValues
    }

    return { min, max }
}

export const getEffectedAlertEntity = (hierarchy, affectedID) => {
    if (hierarchy === 'battery' || !affectedID) {
        return affectedID;
    }

    let packID = 'P1'; // default pack ID
    let moduleID = '';
    let cellID = '';

    let affectedIDLower = affectedID.toLowerCase();

    if (affectedIDLower.includes('_p')) {
        const match = affectedIDLower.split('_p')[1];
        if (match) {
            packID = `P${match.split('_')[0]}`;
        }
    }

    if (affectedIDLower.includes('_m')) {
        const match = affectedIDLower.split('_m')[1];
        if (match) {
            moduleID = `M${match.split('_')[0]}`;
        }
    }

    if (affectedIDLower.includes('_c')) {
        const match = affectedIDLower.split('_c')[1];
        if (match) {
            cellID = `C${match.split('_')[0]}`;
        }
    }

    if (hierarchy === 'pack') {
        return `${packID}`;
    }

    if (hierarchy === 'module') {
        return [packID, moduleID].filter(Boolean).join('/');
    }

    if (hierarchy === 'cell') {
        return [packID, moduleID, cellID].filter(Boolean).join('/');
    }

    return '';
};


export const swapIndexForMultiYAxis = (data) => {
    // In Apex when we have multiple Yaxis it plots the first two series in the opposite order
    // So we need to swap the index of the first two series
    if (!data) {
        return data
    }

    if (data.length < 2) {
        return data
    }

    const copyData = [...data]
    const temp = copyData[0];
    copyData[0] = copyData[1];
    copyData[1] = temp;
    return copyData;
}


export const getAllChildIds = (node) => {
    let ids = [];
    if (node.children && node.children.length > 0) {
        node.children.forEach((child) => {
            ids.push(child.id);
            ids = ids.concat(getAllChildIds(child));
        });
    }
    return ids;
};

export const getNodesAtLevel = (nodes, targetLevel, currentLevel = 0) => {
    let result = [];
    for (const node of nodes) {
        if (currentLevel === targetLevel) {
            result.push(node);
        } else if (node.children) {
            result = result.concat(getNodesAtLevel(node.children, targetLevel, currentLevel + 1));
        }
    }
    return result;
};

export const getNodesAtHierarchyLevel = (nodes, targetHierarchy) => {
    let result = [];
    for (const node of nodes) {
        if (node.level === targetHierarchy) {
            result.push(node);
        } else if (node.children) {
            result = result.concat(getNodesAtHierarchyLevel(node.children, targetHierarchy));
        }
    }
    return result;
};

export const fetchDigitalTwinHierarchy = async (batteryID, entityTypes = [], translation) => {
    try {
        const [hierarchyRes] = await Promise.all([
            getBatteryHierarchyV3(batteryID, entityTypes),
        ]);

        const formattedStructures = {}
        entityTypes.forEach(entityType => {
            const entityTypeValue = hierarchyRes.response[entityType];
            const formattedStructure = transformTwinHierarchyData(entityTypeValue, translation);
            formattedStructures[entityType] = formattedStructure;
        });

        return formattedStructures;
    } catch (error) {
        console.error('Error fetching digital twin hierarchy:', error);
    }
};

export const getBatterySignalsData = async (batteryID, entityType) => {
    const batterySignalsResponse = await getBatterySignals(batteryID, entityType);
    const allSignals = (batterySignalsResponse.response?.data?.signals ?? []);
    const binarySignals = allSignals.filter(signal => signal.signalType.toUpperCase() === 'BINARY');
    const continuousSignals = allSignals.filter(signal => signal.signalType.toUpperCase() === 'CONTINUOUS');

    return { binarySignals, continuousSignals };
}

export const applyColorsToContinuousSignals = (signals) => {
    return signals.map((signal, index) => ({
        ...signal,
        color: axisColors[index],
    }));
}

/**
 * Sticky color allocation (uses axisColors from DataFile):
 * 1) first render: assign from palette order
 * 2) later renders: keep colors for surviving keys
 * 3) new keys: first free color from the palette
 *
 * @param {Object[]} signals
 * @param {Map<string,string>} prevAssignment  key -> color (caller-owned memory)
 * @param {(s:Object)=>string} getKey          how to read the stable key (default: s.key)
 * @returns {{ coloredSignals: Object[], nextAssignment: Map<string,string> }}
 */
export function allocateColors({ signals, prevAssignment, getKey = s => s.key }) {
    const next = new Map();
    const used = new Set();

    // keep colors for surviving signals
    for (const s of signals) {
        const k = getKey(s);
        const prev = prevAssignment?.get(k);
        if (prev) {
            next.set(k, prev);
            used.add(prev);
        }
    }

    // assign colors to newly added signals
    for (const s of signals) {
        const k = getKey(s);
        if (!next.has(k)) {
            const available = axisColors.find(c => !used.has(c));
            const color = available ?? axisColors[next.size % axisColors.length];
            next.set(k, color);
            used.add(color);
        }
    }

    // return colored signals + updated assignment
    const coloredSignals = signals.map(s => ({ ...s, color: next.get(getKey(s)) }));

    return { coloredSignals, nextAssignment: next };
}


export const prepareContinuousSignalsData = (continuousSignals = [], level = '', maxMetrics = 4) => {
    const formattedContinuousSignals = continuousSignals.map(signal => {
        let unit = signal.unitOfMeasurement ?? '';
        if (unit === 'C') { // hardcoding for temperature - to be removed once addressed from backend
            unit = '°C';
        }
        return {
            key: signal.key,
            label: `${signal.label} ${unit ? `(${unit})` : ''}`,
            name: signal.label,
            unit: unit,
            yAxisDecimalPlaces: 2,
            ranges: {},
        }
    });
    let defaultSelectedContinuousSignals = formattedContinuousSignals.slice(0, maxMetrics);
    if (level?.toLowerCase() === 'string') {
        const defaultSignalsKeys = ["voltage", "current", "stringMaxTempCell", "balanceState"];
        if (maxMetrics >= 8){
            defaultSignalsKeys.push("soc", "stringMinTempCell", "stringMaxVoltageCell", "stringMinVoltageCell")
        }
        defaultSelectedContinuousSignals = formattedContinuousSignals.filter(signal => defaultSignalsKeys.includes(signal.key));
    }
    return {
        formattedContinuousSignals,
        defaultSelectedContinuousSignals: applyColorsToContinuousSignals(defaultSelectedContinuousSignals)
    };
}

export const humanizeKey = (key) =>
    key
        .replace(/[_-]+/g, ' ')                     // underscores/hyphens → space
        .replace(/([a-z0-9])([A-Z])/g, '$1 $2')     // split aLowerAUpper -> "a Lower A Upper"
        .replace(/([A-Z])([A-Z][a-z])/g, '$1 $2')   // split "HTTPResponse" -> "HTTP Response"
        .replace(/\s+/g, ' ')                       // collapse multiple spaces
        .trim()
        .replace(/^./, s => s.toUpperCase());

export const countHierarchyEntities = (nodes) => {
    const countOfEntities = {};
    function traverse(nodes) {
        nodes.forEach(node => {
            const nodeLevel = node.level?.toLowerCase();
            if (nodeLevel !== 'battery') {
                if (!countOfEntities[nodeLevel]) {
                    countOfEntities[nodeLevel] = 0;
                }
                countOfEntities[nodeLevel] += 1;
            }

            if (Array.isArray(node.children) && node.children.length > 0) {
                traverse(node.children);
            }
        });
    }
    traverse(nodes)
    return countOfEntities;
};

export const formatHierarchyEntitiesCounts = (counts) => {
    return Object.entries(counts)
        .map(([key, value]) => {
            const capitalized = key.charAt(0).toUpperCase() + key.slice(1);
            const plural = capitalized + 's';
            return `${plural}: ${value}`;
        })
        .join(', ');
}

export const prepareSegmentsFromBinarySignalsData = (signalsData) => {
    const segments = {};
    signalsData.forEach(signalData => {
        const metricsArray = signalData.metricsArray;
        metricsArray.forEach(metricData => {
            if (!segments[metricData.type]) {
                segments[metricData.type] = [];
            }
            segments[metricData.type].push({
                status: metricData.value,
                value: 1,
                timestamp: signalData.batteryDateTime,
            });
        });
    });
    return segments;
}

export const formatBinarySegmentsForChart = (originalSegments = [], targetDate) => {
    const segmentsToFormat = originalSegments ?? [];

    // Create a map for quick lookup of existing timestamps
    const dataMap = new Map();
    segmentsToFormat.forEach(entry => {
        dataMap.set(entry.timestamp, entry);
    });

    // Build the 288-segment output
    const start = new Date(targetDate);
    const segments = [];

    for (let i = 0; i < 288; i++) {
        const current = new Date(start.getTime() + i * 5 * 60 * 1000); // Add 5 minutes
        // Helper to format a date to "YYYY-MM-DD HH:mm:ss"
        const timestamp = current.toISOString().replace("T", " ").substring(0, 19);
        const formattedTimestamp = moment(timestamp).format("DD MMM'YY, hh:mm:ss A");

        if (dataMap.has(timestamp) && dataMap.get(timestamp).status) { // handle status = null also
            const entry = dataMap.get(timestamp);
            segments.push({
                ...entry,
                timestamp: formattedTimestamp,
            });
        } else {
            segments.push({
                timestamp: formattedTimestamp,
                status: "noData",
                value: 1
            });
        }
    }
    return segments;
}

/**
 * Whether Battery Vitals shows the "Analyse > Multiple Battery Parts" comparison
 * link. On unless the page meta sets batteryVitals.showComparison to false.
 */
export const isBatteryVitalsComparisonEnabled = (pageContent) =>
    pageContent?.metaInfo?.batteryVitals?.showComparison !== false;

export const getHierarchyEntityTypes = (pageContent) => {
    let clientHierarchyEntityTypes = pageContent?.metaInfo?.hierarchyEntityTypes || false
    let defaultEntityTypes = ['BATTERY_SYSTEM', 'TMS', 'VEHICLE_SYSTEM']
    if (clientHierarchyEntityTypes) {
        defaultEntityTypes = clientHierarchyEntityTypes
    }
    return defaultEntityTypes
}
